import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Bell, AlertTriangle, FileUp, CheckCircle2, Clock, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = [
  { key: '', label: 'All' },
  { key: 'compliance', label: 'Compliance' },
  { key: 'documents', label: 'Documents' },
];

function getNotifIcon(severity, category) {
  if (category === 'documents') return { icon: FileUp, bg: 'bg-indigo-50', color: 'text-indigo-600' };
  if (severity === 'non_compliant') return { icon: AlertTriangle, bg: 'bg-red-50', color: 'text-red-500' };
  if (severity === 'warning') return { icon: Clock, bg: 'bg-amber-50', color: 'text-amber-600' };
  if (severity === 'compliant') return { icon: CheckCircle2, bg: 'bg-emerald-50', color: 'text-emerald-600' };
  return { icon: Bell, bg: 'bg-slate-50', color: 'text-slate-500' };
}

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'Just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 172800) return 'Yesterday';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function NotificationBell() {
  const { apiCall } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const panelRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const params = category ? `?category=${category}&limit=15` : '?limit=15';
      const res = await apiCall('get', `/notifications${params}`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [apiCall, category]);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiCall('get', '/notifications/unread-count');
      setUnreadCount(res.data.unread_count || 0);
    } catch {
      // silent
    }
  }, [apiCall]);

  // Poll unread count every 30s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch full list when panel opens or category changes
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, category, fetchNotifications]);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const markAllRead = async () => {
    try {
      await apiCall('put', '/notifications/mark-all-read');
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch {
      // silent
    }
  };

  const markRead = async (notifId) => {
    try {
      await apiCall('put', `/notifications/${notifId}/read`);
      setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {
      // silent
    }
  };

  const handleNotifClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    const meta = notif.metadata || {};
    if (meta.employee_id && notif.category === 'compliance') navigate('/pafs');
    else if (meta.employee_id && notif.category === 'documents') navigate('/pafs');
    setOpen(false);
  };

  return (
    <div className="relative" ref={panelRef}>
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell"
        className="relative w-10 h-10 rounded-xl bg-slate-50 border border-slate-200/80 flex items-center justify-center hover:bg-indigo-50 hover:border-indigo-200 transition-all"
      >
        <Bell className="w-[18px] h-[18px] text-slate-500" strokeWidth={1.5} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white" data-testid="notification-badge">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-3 w-[420px] bg-white rounded-2xl border border-slate-200/80 shadow-xl shadow-indigo-100/30 z-50 overflow-hidden"
            data-testid="notification-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100">
              <h3 className="text-base font-bold text-[#0F172A]">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllRead}
                    data-testid="mark-all-read-btn"
                    className="text-xs font-semibold text-indigo-600 hover:bg-indigo-50 px-2.5 py-1.5 rounded-lg transition-colors"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={() => setOpen(false)}
                  className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-slate-100 transition-colors text-slate-400"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex px-6 border-b border-slate-100">
              {CATEGORIES.map(cat => (
                <button
                  key={cat.key}
                  onClick={() => setCategory(cat.key)}
                  data-testid={`notif-tab-${cat.key || 'all'}`}
                  className={`px-4 py-3 text-[13px] font-semibold border-b-2 transition-colors ${
                    category === cat.key
                      ? 'text-indigo-600 border-indigo-600'
                      : 'text-slate-400 border-transparent hover:text-slate-600'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>

            {/* Notification List */}
            <div className="max-h-[380px] overflow-y-auto" data-testid="notification-list">
              {loading && notifications.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-12 px-6">
                  <div className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center mx-auto mb-3">
                    <Bell className="w-6 h-6 text-slate-300" strokeWidth={1.5} />
                  </div>
                  <p className="text-sm text-slate-400 font-medium">No notifications yet</p>
                  <p className="text-xs text-slate-300 mt-1">You'll see updates here when events occur</p>
                </div>
              ) : (
                notifications.map(notif => {
                  const { icon: Icon, bg, color } = getNotifIcon(notif.severity, notif.category);
                  return (
                    <div
                      key={notif.id}
                      onClick={() => handleNotifClick(notif)}
                      data-testid={`notification-item-${notif.id}`}
                      className={`flex gap-3.5 px-6 py-4 border-b border-slate-50 cursor-pointer transition-colors ${
                        notif.read ? 'hover:bg-slate-50' : 'bg-indigo-50/30 hover:bg-indigo-50/50'
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-xl ${bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-[18px] h-[18px] ${color}`} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-[13px] leading-snug mb-0.5 ${notif.read ? 'font-medium text-slate-700' : 'font-semibold text-[#0F172A]'}`}>
                          {notif.title}
                        </p>
                        <p className="text-xs text-slate-500 leading-relaxed line-clamp-2">{notif.message}</p>
                        <p className="text-[11px] text-slate-400 mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      {!notif.read && (
                        <div className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="text-center py-3.5 border-t border-slate-100">
                <button
                  onClick={() => { navigate('/activity'); setOpen(false); }}
                  data-testid="view-all-notifications-btn"
                  className="text-[13px] font-semibold text-indigo-600 hover:text-indigo-700 hover:underline"
                >
                  View all activity
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
